import { appEnvironmentLabel } from "@repo/common";
import { loginWithEmailAndPassword, signInWithGoogle } from "@repo/ui";
import { AlertCircle, Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function LoginPage({
	initialError = "",
	onError,
}: {
	initialError?: string;
	onError?: (message: string) => void;
}) {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isGoogleLoading, setIsGoogleLoading] = useState(false);

	useEffect(() => {
		if (initialError) {
			setError(initialError);
		}
	}, [initialError]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError("");

		if (!email || !password) {
			setError("Please enter email and password");
			return;
		}

		setIsLoading(true);

		try {
			await loginWithEmailAndPassword(email, password);
		} catch (err) {
			const message =
				err instanceof Error
					? err.message
					: "Failed to sign in. Please try again.";
			setError(message);
			// Auth can unmount this form while claims are checked. Keep the result
			// in the shell as well so a replacement form shows the rejection.
			onError?.(message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleSignIn = async () => {
		setIsGoogleLoading(true);
		setError("");

		try {
			await signInWithGoogle();
		} catch (err) {
			if (
				!(
					typeof err === "object" &&
					err !== null &&
					"code" in err &&
					err.code === "auth/popup-closed-by-user"
				)
			) {
				console.error(err);
				const message =
					err instanceof Error
						? err.message
						: "Google sign in failed. Please try again.";
				setError(message);
				onError?.(message);
			}
		} finally {
			setIsGoogleLoading(false);
		}
	};

	return (
		<div className="admin-login">
			<div className="admin-login-layout">
				<div className="admin-login-intro">
					<img src="/provoize_icon.svg" alt="" className="admin-login-logo" />
					<h1>
						Provoize
						<br />
						System administration
					</h1>
					<p>
						Internal workspace for platform operations, customer records, and
						diagnostics.
					</p>
					<span className="admin-login-environment">
						{appEnvironmentLabel} environment
					</span>
				</div>
				<Card className="admin-login-card">
					<CardHeader className="space-y-1">
						<CardTitle className="text-xl font-semibold">Sign in</CardTitle>
						<CardDescription>System administrators only</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<Button
							type="button"
							variant="outline"
							className="w-full flex items-center justify-center gap-2 py-5"
							onClick={handleGoogleSignIn}
							disabled={isGoogleLoading || isLoading}
						>
							{isGoogleLoading ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<svg className="w-5 h-5" viewBox="0 0 24 24">
									<path
										fill="#4285F4"
										d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									/>
									<path
										fill="#34A853"
										d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									/>
									<path
										fill="#FBBC05"
										d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
									/>
									<path
										fill="#EA4335"
										d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
									/>
								</svg>
							)}
							<span>Sign in with Google</span>
						</Button>

						<div className="relative my-4">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-gray-200" />
							</div>
							<div className="relative flex justify-center text-xs uppercase">
								<span className="bg-white px-2 text-gray-500">
									Or continue with email
								</span>
							</div>
						</div>

						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">Email</Label>
								<Input
									id="email"
									type="email"
									autoComplete="email"
									placeholder="admin@example.com"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="password">Password</Label>
								<Input
									id="password"
									type="password"
									autoComplete="current-password"
									placeholder="Enter your password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
							</div>
							<Button
								type="submit"
								className="w-full"
								disabled={isLoading || isGoogleLoading}
							>
								{isLoading ? "Signing In..." : "Sign In"}
							</Button>
						</form>

						{error && (
							<div className="space-y-2 rounded-md bg-red-50 p-3 text-sm text-red-600 border border-red-200 mt-4">
								<div className="flex items-center gap-2">
									<AlertCircle className="h-4 w-4 shrink-0 text-red-600" />
									<span>{error}</span>
								</div>
								{(error.includes("Access denied") ||
									error.includes("privileges")) && (
									<p className="text-xs">
										Contact a system administrator to verify your access.
									</p>
								)}
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
